import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from 'src/reducers';
import { CategoryHeader } from './category-header';
import ProductListing from './components/product-listing';
import type { CompareProductsSelectionType } from '../../hooks/compare-products-selection';
import { useHistory } from 'react-router';
import type { Expert, Products, RenderMode } from './types';
import { sortChange } from '../../globals/actions';
import type { ChangeComparisonSelectionSource } from '../product-compare/tracking';
import queryString from 'querystring';
import AdContainer from '../../components/ads';
import { DesktopOnly, MediumDown } from 'src/ui/block';

export const CategoryProductListing: React.FC<CategoryProductListingProps> = ({
  products,
  defaultLayoutMode,
  immediateProducts,
  storesCount,
  productCount,
  expert,
  categoryName,
  logo,
  compareProductSelection,
  propertySummaryMode = 'verbose',
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const categorySettings = useSelector(
    (state: AppState) => state.areas.category
  );
  const showHorizontalFilter = useSelector(
    (state: AppState) => state.areas.category.filter.showHorizontalFilter
  );

  const { comparing, error: filterError } = categorySettings.filter;
  const showFilter = immediateProducts && !filterError;
  const selectedProperties = products?.pageInfo?.selectedProperties?.filter(x =>
    comparing.includes(x?.id)
  );

  const mode =
    (!immediateProducts && 'grid') ||
    (categorySettings.category && categorySettings.category.mode) ||
    defaultLayoutMode ||
    'grid';

  const selectProductById = useCallback(
    (productId: number, source: string) => {
      const selectedProduct = products.nodes.find(p => p.id === productId);
      if (selectedProduct) {
        compareProductSelection.selectProduct(
          source as ChangeComparisonSelectionSource,
          selectedProduct
        );
      }
    },
    [products, compareProductSelection]
  );

  const deselectProductById = useCallback(
    (productId: number, source: string) => {
      const deselectedProduct = products?.nodes?.find(p => p?.id === productId);
      if (deselectedProduct) {
        compareProductSelection.deselectProduct(
          source as ChangeComparisonSelectionSource,
          deselectedProduct
        );
      }
    },
    [products, compareProductSelection]
  );

  const onSortChange = useCallback(
    (key: string | number | undefined) => {
      if (key == null) return;

      const value = String(key);
      const location = history.location;
      const qs = queryString.decode(location.search.substr(1));

      const selectedSortProperty = value.replace(/:(asc|desc)$/, '');
      const selectedSortDirection = value.match(/:(asc|desc)$/);

      const currentSortDirection = products?.pageInfo?.sort?.direction;

      const data = { replaceHistory: true, value: '' };

      if (selectedSortDirection) {
        // Sort direction was selected from dropdown
        data.value = `${selectedSortProperty}:${selectedSortDirection[1]}`;
      } else if (qs.sort === selectedSortProperty) {
        // Same sort property, then we toggle the current sort direction
        data.value = `${selectedSortProperty}:${
          currentSortDirection === 'asc' ? 'desc' : 'asc'
        }`;
      } else {
        // Changed to a new sort property
        data.value = `${selectedSortProperty}`;
      }

      delete qs.offset;

      dispatch(sortChange(data));
    },
    [dispatch, history.location, products?.pageInfo?.sort?.direction]
  );

  const sortedProducts = useMemo(() => {
    if (!products || immediateProducts) {
      return products;
    }

    // For non-leaf categories, we want to re-sort the fetched products to showcase more sub-categories,
    // and only render the first 12
    return {
      ...products,
      nodes: products.nodes
        ? products.nodes
            .sort((a, b) => {
              if (a?.popularity?.inCategory === b?.popularity?.inCategory) {
                return (a?.popularity?.total || 0) > (b?.popularity?.total || 0)
                  ? 1
                  : -1;
              }

              return (a?.popularity?.inCategory || 0) >
                (b?.popularity?.inCategory || 0)
                ? 1
                : -1;
            })
            .slice(0, 12)
        : products.nodes,
    };
  }, [products, immediateProducts]);

  const productListingAdverts = useMemo(
    () => ({
      desktopInline: (
        <AdContainer
          scalable
          scaleBase=".pj-ui-panel"
          scaleOrigin="left center"
          min={861}
          max={10000}
          spacing={{ bottom: '10px' }}
          {...(immediateProducts
            ? {
                placement: 'panorama_extra',
                format: 'panorama_1_wde',
              }
            : {
                placement: 'panorama_target',
                format: 'panorama_3_wde',
                triggerPoint: 100,
              })}
        />
      ),
    }),
    [immediateProducts]
  );

  const showProductSelector =
    immediateProducts && compareProductSelection.isSelectingProducts;

  const DesktopProductListing = (
    <ProductListing
      header={
        <CategoryHeader
          mode={mode}
          storesCount={storesCount}
          productCount={productCount}
          onSortChange={onSortChange}
          pageInfo={products?.pageInfo}
          immediateProducts={immediateProducts}
          categoryName={categoryName}
          showHorizontalFilter={showHorizontalFilter}
          compareProductSelection={compareProductSelection}
        />
      }
      expert={expert}
      products={sortedProducts}
      selectedProperties={selectedProperties}
      mode={getListRenderMode(mode, immediateProducts, true)}
      pageInfo={products?.pageInfo}
      withFilter={showFilter}
      propertySummaryMode={propertySummaryMode}
      onSortChange={onSortChange}
      fallbackImage={logo}
      showPagination={
        immediateProducts &&
        (products?.pageInfo?.hasNext || products?.pageInfo?.hasPrevious)
      }
      showRatings={immediateProducts}
      showCategory={!immediateProducts}
      showProductSelector={showProductSelector}
      selectedProducts={compareProductSelection.selectedProducts}
      onProductSelect={selectProductById}
      onProductDeselect={deselectProductById}
      adverts={productListingAdverts}
      productCount={productCount}
    />
  );

  const MobileProductListing = React.cloneElement(DesktopProductListing, {
    ...DesktopProductListing.props,
    mode: getListRenderMode(mode, immediateProducts, false),
  });

  return (
    <>
      <DesktopOnly>{DesktopProductListing}</DesktopOnly>
      <MediumDown>{MobileProductListing}</MediumDown>
    </>
  );
};

interface CategoryProductListingProps {
  propertySummaryMode?: string;
  products: Products;
  defaultLayoutMode?: 'grid' | 'row';
  immediateProducts: boolean;
  storesCount: number;
  productCount: number;
  expert: Expert;
  categoryName: string;
  showHorizontalFilter: boolean;
  logo: string;
  compareProductSelection: CompareProductsSelectionType;
}

function getListRenderMode(
  mode: RenderMode,
  immediateProducts: boolean,
  isDesktop: boolean | undefined
) {
  // Render row mode as a table if category has filter and everything
  if (mode === 'row' && immediateProducts && isDesktop) {
    return 'table';
  }

  return mode;
}
